import React, { useState, useContext } from "react"
import { EditorState, ContentState, convertToRaw } from "draft-js"

import addMessageToThread from "../util/fetchApiHelpers/addMessageToThread"
import JamEditor from "./JamEditor"
import type { Thread, Mention } from "../types"
import { ConfettiContext } from "./Confetti"
import { UserContext } from "./UserContext"

export interface JamThreadEditorProps {
	JAM_ID: string
	mentions: Mention[]
	thread: Thread
	onThreadChange: (newThread: Thread) => void
	loading: boolean
	onLoadingChange: (loading: boolean) => void
	socket: SocketIOClient.Socket
}

export default function JamThreadEditor(props: JamThreadEditorProps) {
	let [editorState, setEditorState] = useState(() => EditorState.createEmpty())
	const { celebrate } = useContext(ConfettiContext)
	const { user: loggedInUser, updateUser } = useContext(UserContext)

	function handleEditorChange(newEditorState: EditorState) {
		setEditorState(newEditorState)
	}

	async function handleMessageSend() {
		let message = convertToRaw(editorState.getCurrentContent())

		// start loading
		props.onLoadingChange(true)

		let newThread = await addMessageToThread(
			props.thread._id,
			JSON.stringify(message),
			props.JAM_ID,
		)

		if (newThread) {
			props.onThreadChange(newThread)
		}

		// stop loading
		props.onLoadingChange(false)

		if (loggedInUser && loggedInUser.commentCount === 0) {
			celebrate()
		}

		if (loggedInUser) {
			updateUser({ commentCount: loggedInUser.commentCount + 1 })
		}

		props.socket.emit("threadUpdate", newThread)
		// reset editor
		setEditorState(
			EditorState.push(
				editorState,
				ContentState.createFromText(""),
				"remove-range",
			),
		)
	}

	return (
		<div className="jam--thread-editor-container">
			<div className="jam--thread-header-container">
				{loggedInUser && (
					<div style={{ display: "flex" }}>
						<img
							className="jam--thread-header-author-avatar"
							src={loggedInUser.avatar}
						/>
						<div className="jam--thread-header-author-name">
							{loggedInUser.name}
						</div>
					</div>
				)}
				<div
					onClick={handleMessageSend}
					className="jam--button jam--thread-editor-send-button"
				>
					{props.loading ? "Sending..." : "Send"}
					<img
						className="jam--tiny-berry"
						src="https://strawberryjam.nyc3.cdn.digitaloceanspaces.com/icon.png"
					/>
				</div>
			</div>
			<JamEditor
				onEditorChange={handleEditorChange}
				editorState={editorState}
				onMessageSend={handleMessageSend}
				mentions={props.mentions}
			/>
			<style>
				{`
						.jam--thread-editor-container {
							align-content: center;
							display: flex;
							flex-direction: column;
							align-items: center;
							width: 100%;
							font-size: 14px;
							z-index: 1000000;
						}

						.jam--thread-editor-container:before {
							content: " ";
							display: block;
							border-bottom: 1px solid #eaeaea;
							font-size: 14px;
						}

						.jam--thread-editor-textarea {
							background: #fff;
							border: 1px solid #fff;
							color: #2a3632;
							font-family: "SF Pro Text", -apple-system, BlinkMacSystemFont,
								"Segoe UI", Roboto, "Helvetica Neue", Arial, "Noto Sans",
								sans-serif, "Apple Color Emoji", "Segoe UI Emoji",
								"Segoe UI Symbol", "Noto Color Emoji";
							font-size: 14px;
							resize: none;
							width: 240px;
							display: flex;
							flex-direction: column;
							justify-content: center;
							margin-left: 18px;
							font-size: 14px;
							outline: none;
							border: none;
							text-align: left;
						}

						.jam--thread-editor-textarea:focus {
							outline: 0px !important;
							-webkit-appearance: none;
							box-shadow: none !important;
						}

						.jam--thread-editor-send-button {
							align-content: flex-end;
							transition: all 0.4s ease-in-out;
							padding: 10px 24px;
							border: 1px solid #3c81c7;
							box-sizing: border-box;
							border-radius: 124px;
							display: flex;
							align-items: center;
							justify-content: center;
							height: 40px;
							color: #2a3632;
							font-size: 14px;
							line-height: 20px;
							margin: 0;
							font-weight: 500;
							background: rgba(100, 175, 250, 0.15);
							color: #3c81c7;
						}

						.jam--thread-editor-send-button:hover {
							cursor: pointer;
						}

						.jam--tiny-berry {
							width: 20px;
							margin-left: 8px;
						}

						.jam--reaction-avatar {
							border-radius: 50%;
							height: 40px;
							width: 40px;
						}

						.DraftEditor-alignLeft .public-DraftEditorPlaceholder-root {
							left: 0;
							text-align: left;
						}
						.DraftEditor-alignCenter .public-DraftEditorPlaceholder-root {
							margin: 0 auto;
							text-align: left;
							width: 100%;
						}
						.DraftEditor-alignRight .public-DraftEditorPlaceholder-root {
							right: 0;
							text-align: right;
						}
						.public-DraftEditorPlaceholder-root {
							color: #9197a3;
							position: absolute;
							z-index: 1;
						}

						.public-DraftEditorPlaceholder-hasFocus {
							color: #bdc1c9;
						}

						.DraftEditorPlaceholder-hidden {
							display: none;
						}
					`}
			</style>
		</div>
	)
}
